import { type MaybeRef, unref } from 'vue';
import { useUtilities } from './useUtilities';

type KeyOf<T extends object> = {
  [K in keyof T]?: T[K]
}

export const useChangedFieldObserver = () => {
  const { isObject, isEmptyObject } = useUtilities();

  const areEqual = (a: unknown, b: unknown) => {
    if (a instanceof File || b instanceof File) {
      return false;
    }

    if (!a && !b) {
      return true;
    }
    if ((a && !b) || (!a && b)) {
      return false;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      const sortedA = a.toSorted();
      const sortedB = b.toSorted();

      return JSON.stringify(sortedA) === JSON.stringify(sortedB);
    }

    return JSON.stringify(a) === JSON.stringify(b);
  };

  const buildProxy = <T extends object>(data: T, changes: Partial<T>): T => new Proxy(data, {
    get(target, p, receiver) {
      if (Array.isArray(target[p])) {
        changes[p] = [...target[p]];
      }

      return Reflect.get(target, p, receiver);
    },
    set(target, p, newValue, receiver) {
      if (!areEqual(newValue, target[p])) {
        Reflect.set(changes, p, newValue);
      }

      return Reflect.set(target, p, newValue, receiver);
    },
  });

  const observeChanges = <T extends object>(sourceData: MaybeRef<T>, alwaysInclude?: KeyOf<T>): [changes: Partial<T>, proxy: T] => {
    const data = unref(sourceData);
    const changes = isObject(alwaysInclude) && !isEmptyObject(alwaysInclude) ? { ...alwaysInclude } : {};

    const proxy = buildProxy(data, changes);

    return [changes, proxy];
  };

  return { observeChanges };
};
